
import React, { useState, useEffect, useMemo } from 'react';
import TokenBadge from './shared/TokenBadge';
import { selectPrimaryCredits } from '../utils/creditSelector';
import { Project, TranslationFile, JobStatus, SynonymGroup } from '../types';

import { ArrowLeft, ArrowRight, Settings, FilePlus, FileText, Play, Trash2, CheckCircle, XCircle, Loader2, Clock, Eye, AlertTriangle, BookOpen, BookMarked, Download, Plus, X, Check, Edit2, RefreshCw, AlertCircle, Zap, Sparkles, Maximize2, Minimize2, ChevronDown, HelpCircle } from 'lucide-react';
import IndustryPresetModal from './commercial/IndustryPresetModal';
import type { IndustryPreset } from '../utils/industryPrompts';
import { isCommercial } from '../config/appMode';
import LanguagePicker from './commercial/OnboardingTour/LanguagePicker';
import { getDefaultLocale } from '../utils/locales';
import LocalePicker from './LocalePicker';
import { getLanguage, getLanguageNameVi } from '../utils/languages';
import BatchUploadModal from './modals/BatchUploadModal';
import Tooltip from './Tooltip';
import TerminologyTab from './TerminologyTab';
import TranslateTab from './TranslateTab';
import RunAllModal from './commercial/RunAllModal';
import { useCredits } from '../hooks/useCredits';
import { useTourStore } from '../store/useTourStore';
import { FilePair } from '../utils/fileMatcher';
import { aggregateProjectComments, deduplicateRules, ParsedCommentWithMeta } from '../utils/commentParser';
import { BatchExecutionPanel, useBatchSchedulerStore, useCreatePlan, useExecutePlan, createTranslateExecutionPlan } from '../features/batch-scheduler';
import TerminologyImportPanel from '../features/terminology/ui/TerminologyImportPanel';
import TermSelector from './TermSelector';
import SynonymGroupEditor from './SynonymGroupEditor';
import { useGlossaryStore } from '../store/useGlossaryStore';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';
import { useFileSystemStore } from '../store/useFileSystemStore';
import { useProjectStore } from '../store/useProjectStore';
import { sanitizeFolderName } from '../utils/textUtils';
import { useEstimateBackfill } from '../hooks/useEstimateBackfill';
import { triggerRecovery } from '../hooks/useBatchRecovery';
import { parseMatchedRulesString } from '../utils/highlightTerms';
import { findMatchingSynonymGroupsForSegments } from '../utils/glossaryUtils';
import { DEMO_PROJECT_ID, DEMO_TERMS } from '../utils/demoProject';

interface ProjectDetailProps {
    project: Project;
    files: TranslationFile[];
    onBack: () => void;
    onSelectFile: (fileId: string) => void;
    onUpdateProject: (project: Project) => void;
    onAddFiles: (files: File[]) => void;
    onBatchImport: (pairs: FilePair[]) => void;
    onDeleteFile: (fileId: string) => void;
    onRunFile: (fileId: string) => Promise<void> | void;
    onRunTranslate: (fileId: string) => Promise<void> | void;
    onRunQC?: (fileId: string) => Promise<void> | void;
    onRunAll: () => void;
    onExportProject: () => void;
    initialTab?: 'translate' | 'train' | 'glossary' | 'terminology' | 'report';
    returnToFileId?: string | null;
    onReturnToFile?: (fileId: string) => void;
    streamingCount?: number;
    totalSegments?: number;
    activeTranslatingFileId?: string;
}

// Helper to highlight text
const HighlightText = ({ text, highlight, color = 'bg-yellow-200' }: { text: string; highlight: string; color?: string }) => {
    if (!highlight || !text) return <>{text}</>;

    // Escape regex special characters
    const safeHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${safeHighlight})`, 'gi'));

    return (
        <span>
            {parts.map((part, i) =>
                part.toLowerCase() === highlight.toLowerCase() ?
                    <span key={i} className={`${color} font-medium px-0.5 rounded`}>{part}</span> :
                    part
            )}
        </span>
    );
};


// Helper Component: StepStatus
const StepStatus: React.FC<{ file: TranslationFile; hasProjectBenchmark: boolean }> = ({ file, hasProjectBenchmark }) => {
    // Check if semantic match has issues (high unmatched count) - for tooltip only, not error state
    const matchResult = file.semanticMatchResult;
    const hasMatchIssue = matchResult && matchResult.unmatchedCount > matchResult.mappings.length * 0.5;
    // Match is done if status says so OR we have a result
    const matchDone = file.matchStatus === 'done' || !!file.semanticMatchResult;

    // Audit status: only flag as error if the step itself failed (system error)
    // Quality scores are shown separately in the Score column
    const auditResult = file.auditResult;

    const steps = [
        { step: 1, label: 'Nguồn', done: true, error: false, usage: null as any },
        { step: 2, label: 'Đối chiếu', done: !!file.humanBenchmark || hasProjectBenchmark, error: false, usage: null as any },
        { step: 2.5, label: 'Ghép cặp', done: matchDone, error: file.matchStatus === 'error' || (matchDone && hasMatchIssue), usage: file.semanticMatchResult?.usage || null },
        { step: 3, label: 'Dịch', done: file.translationStatus === 'done', error: file.translationStatus === 'error', usage: file.translationUsage || null },
        { step: 4, label: 'Đánh giá', done: file.auditStatus === 'done', error: file.auditStatus === 'error', usage: file.auditUsage || null }
    ];

    return (
        <div className="flex items-center gap-1.5">
            {steps.map((item, idx) => {
                // Check if this step is running
                // Parallel execution: Match and Audit can be running simultaneously
                const isRunning =
                    (item.step === 2.5 && file.matchStatus === 'running') ||
                    (item.step === 3 && file.translationStatus === 'running') ||
                    (item.step === 4 && file.auditStatus === 'running');

                // Current if step matches currentStep or is simply running
                const isCurrent = file.currentStep === item.step || isRunning;

                // Determine icon and colors
                let bgClass = 'bg-slate-100 text-slate-400';
                let icon: React.ReactNode = <span className="text-[10px] font-bold">{item.step}</span>;

                if (item.error) {
                    bgClass = 'bg-red-100 text-red-600';
                    icon = <AlertCircle className="w-3.5 h-3.5" />;
                } else if (item.done) {
                    bgClass = 'bg-emerald-100 text-emerald-600';
                    icon = <CheckCircle className="w-3.5 h-3.5" />;
                } else if (isRunning) {
                    bgClass = 'bg-blue-100 text-blue-600 animate-pulse';
                    icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
                } else if (isCurrent) {
                    bgClass = 'bg-indigo-600 text-white shadow-sm';
                }

                return (
                    <React.Fragment key={item.step}>
                        {idx > 0 && (
                            <div className={`w-3 h-px ${item.done || item.error || isCurrent ? 'bg-indigo-300' : 'bg-slate-200'}`} />
                        )}
                        <div
                            className={`flex items-center justify-center rounded-full transition-all shrink-0 w-6 h-6 ${bgClass} cursor-default`}
                            title={(() => {
                                let tooltip = item.label;
                                if (item.error) tooltip += ' - Lỗi';
                                if (item.usage) {
                                    const u = item.usage;
                                    tooltip += `\nInput: ${(u.inputTokens || 0).toLocaleString()} tokens`;
                                    tooltip += `\nOutput: ${(u.outputTokens || 0).toLocaleString()} tokens`;
                                    tooltip += `\nTotal: ${(u.totalTokens || 0).toLocaleString()} tokens`;
                                    if (u.cost) tooltip += `\nCost: $${u.cost.toFixed(4)}`;
                                } else if (item.done && item.step <= 2) {
                                    // Source/Benchmark - no usage
                                } else if (!item.done && !isRunning) {
                                    tooltip += ' - Chờ';
                                } else if (isRunning) {
                                    tooltip += ' - Đang chạy...';
                                }
                                return tooltip;
                            })()}
                        >
                            {icon}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const ProjectDetail: React.FC<ProjectDetailProps> = ({
    project,
    files,
    onBack,
    onSelectFile,
    onUpdateProject,
    onAddFiles,
    onBatchImport,
    onDeleteFile,
    onRunFile,
    onRunTranslate,
    onRunQC,
    onRunAll,
    onExportProject,
    initialTab,
    returnToFileId,
    onReturnToFile,
    streamingCount = 0,
    totalSegments = 0,
    activeTranslatingFileId
}) => {
    const [showSettings, setShowSettings] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Reactive subscribe Global Glossary store + ensure loaded on mount.
    // Trước fix này: dùng useGlossaryStore.getState() = snapshot 1 lần, không re-render khi store update
    // → Tab Thuật ngữ mở ra với data rỗng, chỉ hiện sau khi user nhảy qua tab Từ điển thuật ngữ.
    const globalSynonymGroups = useGlossaryStore(s => s.synonymGroups);
    const isGlossaryLoaded = useGlossaryStore(s => s.isLoaded);
    const loadGlossary = useGlossaryStore(s => s.loadGlossary);
    React.useEffect(() => {
        if (!isGlossaryLoaded) loadGlossary();
    }, [isGlossaryLoaded, loadGlossary]);

    // Backfill estimatedCredits cho file thiếu cache (lazy compute via /api/count-tokens-bundle)
    useEstimateBackfill(project?.id || null);

    // Trigger pending_sync recovery ngay khi user vào project (không đợi 5s poll cycle)
    // Bắt kết quả train chain ngay → "Bắt đầu" → "Hoàn thành" không nháy
    useEffect(() => {
        if (project?.id) triggerRecovery();
    }, [project?.id]);

    // Train tab: matched rules indicator state + helper
    const [trainRulesModalFileId, setTrainRulesModalFileId] = useState<string | null>(null);
    const trainGetMatchedRules = (file: TranslationFile): { source: string; target: string }[] => {
        if (file.matchedRules) return parseMatchedRulesString(file.matchedRules);
        const groups = localProject.synonymGroups || [];
        if (groups.length === 0) return [];
        const searchText = file.sourceText && file.sourceText.length > 0
            ? file.sourceText
            : (file.backendSegments && file.backendSegments.length > 0)
                ? file.backendSegments.join('\n')
                : '';
        if (!searchText) return [];
        const excludedSet = new Set((file.excludedRules || []).map(r => r.toLowerCase()));
        return findMatchingSynonymGroupsForSegments(searchText, groups)
            .filter((m: any) => !excludedSet.has(m.match.toLowerCase()))
            .map((m: any) => ({
                source: m.match,
                target: m.group.targets.find((t: any) => t.isDefault)?.text || m.group.targets[0]?.text || ''
            }));
    };

    // Persist project metadata (synonymGroups / importedTerminology / etc.) to disk immediately.
    // Tránh lệ thuộc auto-save debounce — refresh trước khi auto-save fire = mất data.
    const persistProjectMetadata = (updated: any, source: string) => {
        const rootHandle = useFileSystemStore.getState().rootHandle;
        if (!rootHandle) return;
        (fileSystemPersistence as any).handle = rootHandle;
        const category = project.type === 'translate' ? 'translating' : project.type === 'train' ? 'training' : '';
        const folderName = `project_${sanitizeFolderName(project.name, project.id)}`;
        const path = category ? [category, folderName] : [folderName];
        const { files: _f, ...metadata } = updated as any;
        fileSystemPersistence.saveJson(path, 'project.json', metadata)
            .then(() => console.log(`[${source}] Saved project.json to disk`))
            .catch(err => console.error(`[${source}] Disk save failed:`, err));
    };
    const getValidInitialTab = () => {
        const validTrainTabs = ['train', 'glossary', 'report', 'settings'];
        const validTranslateTabs = ['translate', 'terminology', 'settings'];

        if (project.type === 'translate') {
            // Translate project: only allow translate/terminology/settings tabs
            if (initialTab && validTranslateTabs.includes(initialTab)) return initialTab;
            return 'translate';
        } else {
            // Train project (default): only allow train/glossary/settings tabs
            if (initialTab && validTrainTabs.includes(initialTab)) return initialTab;
            return 'train';
        }
    };
    const [activeTab, setActiveTab] = useState<'translate' | 'train' | 'glossary' | 'terminology' | 'report' | 'settings'>(getValidInitialTab() as any);
    const [localProject, setLocalProject] = useState(project);
    // Sync thuật ngữ khi store update bên ngoài (vd: demo patch trong tour)
    useEffect(() => {
        setLocalProject((prev: Project) => ({
            ...prev,
            synonymGroups: project.synonymGroups,
            importedTerminology: project.importedTerminology,
        }));
    }, [project.synonymGroups, project.importedTerminology]);
    const [showPresetModal, setShowPresetModal] = useState(false);
    const [showPromptFullscreen, setShowPromptFullscreen] = useState(false);
    const [showAddToTerminologyModal, setShowAddToTerminologyModal] = useState(false);
    const [showImportPanel, setShowImportPanel] = useState(false);
    const [showTermSelector, setShowTermSelector] = useState(false);
    const [showGroupEditor, setShowGroupEditor] = useState(false);
    const [pendingRules, setPendingRules] = useState<Array<{
        source: string;
        target: string;
        selected: boolean;
        originalSource: string;
        originalTarget: string;
        isDuplicate: boolean;
    }>>([]);
    const [selectedTargetGroupId, setSelectedTargetGroupId] = useState<string>('');

    // Batch Scheduler
    const { showPanel: showBatchPanel, togglePanel: toggleBatchPanel, currentPlan } = useBatchSchedulerStore();
    const createPlanMutation = useCreatePlan();
    const [showTrainAll, setShowTrainAll] = useState(false);
    const { balance: creditBalance } = useCredits();
    const executePlanMutation = useExecutePlan({
        runFileAuto: async (fileId) => {
            if (project.type === 'translate') {
                await onRunTranslate(fileId);
            } else {
                await onRunFile(fileId);
            }
        }
    });

    // Sync initialTab when it changes
    useEffect(() => {
        if (initialTab) setActiveTab(initialTab);
    }, [initialTab]);

    // Force activeTab to be valid for current project type (runs every render)
    const validTrainTabs = ['train', 'glossary', 'report', 'settings'];
    const validTranslateTabs = ['translate', 'terminology', 'settings'];
    const validatedActiveTab = project.type === 'translate'
        ? (validTranslateTabs.includes(activeTab) ? activeTab : 'translate')
        : (validTrainTabs.includes(activeTab) ? activeTab : 'train');

    // Sync if validated tab differs from current
    if (validatedActiveTab !== activeTab) {
        // Use setTimeout to avoid setState during render
        setTimeout(() => setActiveTab(validatedActiveTab as any), 0);
    }

    // [REMOVED] Backward Compatibility useEffect -- synonymGroups is now the single source of truth

    const projectFiles = files.filter(f => f.projectId === project.id);
    const trainFiles = projectFiles.filter(f => !!f.humanBenchmark);
    const translateFiles = projectFiles.filter(f => !f.humanBenchmark);

    // Migration: hydrate auditScore cho file audited TRƯỚC khi có field auditScore.
    // Trigger lazy-load để Worker đọc audit.json từ disk → fill auditResult vào store.
    // Sau khi hydrate, file.auditResult.overallScore sẵn sàng cho table render (cả Train + Translate QC).
    const filesNeedingScoreHydration = [...trainFiles, ...translateFiles]
        .filter((f: TranslationFile) => f.auditStatus === 'done' && f.auditScore == null && !f.auditResult)
        .map((f: TranslationFile) => f.id)
        .sort()
        .join(',');
    React.useEffect(() => {
        if (!filesNeedingScoreHydration) return;
        let cancelled = false;
        (async () => {
            const { ensureFileContentLoaded } = await import('../hooks/useFileSystem');
            const ids = filesNeedingScoreHydration.split(',');
            for (const id of ids) {
                if (cancelled) return;
                try { await ensureFileContentLoaded(id); } catch (e) { console.warn('[Migrate audit score]', id, e); }
            }
        })();
        return () => { cancelled = true; };
    }, [filesNeedingScoreHydration]);

    // Aggregate Analysis from parsed comments
    const parsedComments = aggregateProjectComments(projectFiles, project.humanBenchmark);
    const parsedRules = deduplicateRules(parsedComments.rule);

    // Legacy: Keep criticalErrors for backward compatibility
    const allErrors = projectFiles.flatMap(f =>
        (f.auditResult?.criticalErrors || []).map(e => ({ ...e, fileId: f.id, fileName: f.fileName }))
    );

    // Aggregate glossary rules from all files (deduplicated)
    const allGlossaryRules = (() => {
        const rulesMap = new Map<string, { source: string; target: string; type: string; reason: string; count: number }>();
        projectFiles.forEach(f => {
            (f.auditResult?.glossaryRules || []).forEach(rule => {
                const key = `${rule.source}::${rule.target}`;
                if (rulesMap.has(key)) {
                    rulesMap.get(key)!.count++;
                } else {
                    rulesMap.set(key, { ...rule, reason: rule.reason || '', count: 1 });
                }
            });
        });
        return Array.from(rulesMap.values()).sort((a, b) => b.count - a.count);
    })();

    // Group rules by type
    const rulesByType = {
        terminology: allGlossaryRules.filter(r => r.type === 'terminology'),
        format: allGlossaryRules.filter(r => r.type === 'format'),
        style: allGlossaryRules.filter(r => r.type === 'style'),
    };

    // New stats from parsed comments
    const stats = {
        loi: parsedComments.loi.length,
        rule: parsedComments.rule.length,
        canxemlai: parsedComments.canxemlai.length,
    };

    const StatusIcon: React.FC<{ status: JobStatus }> = ({ status }) => {
        switch (status) {
            case 'done':
                return <CheckCircle className="w-4 h-4 text-emerald-500" />;
            case 'running':
                return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
            case 'error':
                return <XCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Clock className="w-4 h-4 text-slate-400" />;
        }
    };

    const getStatusText = (status: JobStatus) => {
        switch (status) {
            case 'done': return 'Xong';
            case 'running': return 'Đang chạy';
            case 'error': return 'Lỗi';
            default: return 'Chờ';
        }
    };



    const getScoreColor = (score: number) => {
        if (score >= 8) return 'text-emerald-500';
        if (score >= 6) return 'text-amber-500';
        return 'text-red-500';
    };

    const handleFileInput = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = '.md,.docx,.pptx';
        input.onchange = (e) => {
            const files = (e.target as HTMLInputElement).files;
            if (files) {
                onAddFiles(Array.from(files));
            }
        };
        input.click();
    };

    const handleSaveSettings = () => {
        onUpdateProject(localProject);
        setActiveTab(project.type === 'translate' ? 'translate' : 'train');
    };

    return (
        <>
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="shrink-0 px-6 py-4 border-b border-slate-200 bg-white shadow-sm relative flex items-center z-10">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-slate-800 tracking-tight">{project.name || 'Dự án chưa đặt tên'}</h1>
                            <div className="flex items-center gap-2 text-sm text-slate-500 mt-0.5">
                                <span>{projectFiles.length} tài liệu</span>
                                {(() => {
                                    const src = getLanguage(localProject.sourceLang || 'vi');
                                    const tgt = getLanguage(localProject.targetLang || 'en');
                                    if (!src || !tgt) return null;
                                    return (
                                        <>
                                            <span className="text-slate-300">·</span>
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('settings')}
                                                className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded-md transition-colors group"
                                                title="Click để đổi ngôn ngữ trong Cấu hình"
                                            >
                                                <img
                                                    src={`https://flagcdn.com/20x15/${src.countryCode}.png`}
                                                    srcSet={`https://flagcdn.com/40x30/${src.countryCode}.png 2x`}
                                                    width="20" height="15"
                                                    alt=""
                                                    loading="lazy"
                                                    className="rounded-sm"
                                                />
                                                <span className="text-xs font-semibold text-slate-700 group-hover:text-indigo-700">{src.code.toUpperCase()}</span>
                                                <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-indigo-500" />
                                                <img
                                                    src={`https://flagcdn.com/20x15/${tgt.countryCode}.png`}
                                                    srcSet={`https://flagcdn.com/40x30/${tgt.countryCode}.png 2x`}
                                                    width="20" height="15"
                                                    alt=""
                                                    loading="lazy"
                                                    className="rounded-sm"
                                                />
                                                <span className="text-xs font-semibold text-slate-700 group-hover:text-indigo-700">{tgt.code.toUpperCase()}</span>
                                            </button>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Tabs - centered absolutely */}
                    <div className="absolute left-1/2 -translate-x-1/2 flex bg-slate-100 p-1 rounded-lg">
                        {project.type === 'translate' ? (
                            <>
                                {/* Translate Project: Translate + Terminology tabs */}
                                <button
                                    onClick={() => setActiveTab('translate')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'translate' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                                    Dịch
                                </button>
                                <button
                                    onClick={() => setActiveTab('terminology')}
                                    data-tour="tab-terminology"
                                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'terminology' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Thuật ngữ
                                </button>
                            </>
                        ) : (
                            <>
                                {/* Train Project: Train + Glossary tabs only */}
                                <button
                                    onClick={() => setActiveTab('train')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'train' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                    Huấn luyện
                                </button>
                                <button
                                    onClick={() => setActiveTab('report')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'report' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    Báo cáo chất lượng
                                    {allErrors.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full">{allErrors.length}</span>}
                                </button>
                                <button
                                    onClick={() => setActiveTab('glossary')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === 'glossary' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Bảng thuật ngữ
                                    {(localProject.synonymGroups || []).length > 0 && <span className="bg-emerald-500 text-white text-[10px] px-1.5 rounded-full">{(localProject.synonymGroups || []).length}</span>}
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => setActiveTab('settings')}
                            data-tour="tab-settings"
                            className={`px-4 py-1.5 ml-2 rounded-md text-sm font-semibold transition-all flex items-center gap-2 border-l border-slate-200 ${activeTab === 'settings' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <Settings className="w-4 h-4" />
                            Cấu hình
                        </button>
                    </div>



                    <div className="flex items-center gap-2">
                        {localProject.type === 'translate' && (
                            <button
                                onClick={() => window.dispatchEvent(new CustomEvent('restart-stage4-tour'))}
                                className="p-1.5 rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                title="Xem lại hướng dẫn tab Dịch"
                            >
                                <HelpCircle className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-hidden relative">
                    {/* Demo project banner */}
                    {localProject.isDemo && (
                        <div className="px-6 pt-4">
                            <div className="flex items-start gap-3 px-4 py-3 bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border border-purple-200 rounded-xl">
                                <Sparkles className="w-5 h-5 text-purple-500 mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-purple-900">Đây là project DEMO để hướng dẫn</p>
                                    <p className="text-xs text-purple-700/80 mt-0.5">
                                        File mẫu đã có sẵn để bạn xem giao diện. Khi xong tour, quay lại danh sách và bấm <strong>"Dự án mới"</strong> để tạo project thật của bạn.
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        useTourStore.getState().end();
                                        onBack();
                                    }}
                                    className="px-3 py-1.5 text-xs font-bold text-purple-700 bg-white border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors shrink-0"
                                >
                                    Quay lại
                                </button>
                            </div>
                        </div>
                    )}

                    {/* SETTINGS CONTENT */}
                    {validatedActiveTab === 'settings' && (
                        <div className="h-full overflow-y-auto p-6 bg-slate-50">
                            <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                                <h2 className="text-xl font-bold mb-6 text-slate-800 flex items-center gap-2">
                                    <Settings className="w-5 h-5 text-slate-500" />
                                    Cấu hình dự án
                                </h2>

                                <div className="space-y-8">
                                    <section data-tour="settings-name">
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tên dự án</label>
                                        <input
                                            type="text"
                                            value={localProject.name}
                                            onChange={(e) => setLocalProject({ ...localProject, name: e.target.value })}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-slate-50 focus:bg-white"
                                        />
                                    </section>

                                    <section data-tour="settings-lang" className="pt-6 border-t border-slate-100">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-1">Cặp ngôn ngữ dịch</h3>
                                        <p className="text-xs text-slate-500 mb-3">Chọn ngôn ngữ gốc của tài liệu và ngôn ngữ bạn muốn dịch sang.</p>
                                        <div className="flex items-end gap-3">
                                            <div className="flex-1 min-w-0">
                                                <LanguagePicker
                                                    value={localProject.sourceLang || 'vi'}
                                                    onChange={(code) => setLocalProject({ ...localProject, sourceLang: code })}
                                                    excludeCode={localProject.targetLang}
                                                />
                                            </div>
                                            <div className="pb-2.5">
                                                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <LanguagePicker
                                                    value={localProject.targetLang || 'en'}
                                                    onChange={(code) => setLocalProject({ ...localProject, targetLang: code })}
                                                    excludeCode={localProject.sourceLang}
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    <section data-tour="settings-prompt" className="pt-6 border-t border-slate-100">
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                            <div>
                                                <h3 className="text-sm font-semibold text-slate-700">Chỉ thị cho AI</h3>
                                                <p className="text-xs text-slate-500">Hướng dẫn phong cách dịch. Ghi đè chỉ thị mặc định toàn hệ thống.</p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPromptFullscreen(true)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg border border-slate-200 hover:bg-slate-200 hover:border-slate-300 transition-all active:scale-95"
                                                    title="Phóng to để chỉnh prompt"
                                                >
                                                    <Maximize2 className="w-3.5 h-3.5" />
                                                    Phóng to
                                                </button>
                                                {isCommercial && (
                                                    <button
                                                        type="button"
                                                        data-tour="settings-preset"
                                                        onClick={() => setShowPresetModal(true)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 transition-all active:scale-95"
                                                        title="Chọn preset theo ngành"
                                                    >
                                                        <Sparkles className="w-3.5 h-3.5" />
                                                        Chọn preset ngành
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <textarea
                                                value={localProject.systemPrompt}
                                                onChange={(e) => setLocalProject({ ...localProject, systemPrompt: e.target.value })}
                                                className="w-full h-40 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none transition-all text-sm font-mono bg-slate-50 focus:bg-white"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPromptFullscreen(true)}
                                                className="absolute top-2 right-2 p-1.5 bg-white border border-slate-200 rounded-md text-slate-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition-all opacity-70 hover:opacity-100"
                                                title="Phóng to để chỉnh prompt"
                                                aria-label="Phóng to prompt"
                                            >
                                                <Maximize2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </section>

                                    {/* Output language for AI report (QC for translate, Audit for train) */}
                                    <section data-tour="settings-qc-lang" className="pt-6 border-t border-slate-100">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-1">
                                            {project.type === 'train' ? 'Ngôn ngữ báo cáo Audit' : 'Ngôn ngữ báo cáo QC'}
                                        </h3>
                                        <p className="text-xs text-slate-500 mb-3">
                                            {project.type === 'train'
                                                ? 'AI sẽ viết note, rule và mô tả lỗi trong báo cáo audit bằng ngôn ngữ này.'
                                                : 'AI sẽ viết đánh giá QC, mô tả lỗi và gợi ý cải thiện bằng ngôn ngữ này.'}
                                        </p>
                                        {project.type === 'train' ? (
                                            <LanguagePicker
                                                value={localProject.auditOutputLang || 'vi'}
                                                onChange={(code) => setLocalProject({ ...localProject, auditOutputLang: code })}
                                            />
                                        ) : (
                                            <LanguagePicker
                                                value={localProject.qcOutputLang || 'vi'}
                                                onChange={(code) => setLocalProject({ ...localProject, qcOutputLang: code })}
                                            />
                                        )}
                                    </section>

                                    <section data-tour="settings-locale" className="pt-6 border-t border-slate-100">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-1">Định dạng số &amp; ngày</h3>
                                        <p className="text-xs text-slate-500 mb-2">
                                            Cách AI format số và ngày trong bản dịch. Chọn <b>Tự động</b> để dùng locale mặc định theo ngôn ngữ đích.
                                        </p>
                                        <LocalePicker
                                            value={localProject.locale || ''}
                                            onChange={(code) => setLocalProject({ ...localProject, locale: code })}
                                            autoLabel={`Tự động — theo ngôn ngữ đích (${getDefaultLocale(localProject.targetLang)})`}
                                        />
                                    </section>

                                    <div className="pt-6 border-t border-slate-100 flex gap-3">
                                        <button
                                            data-tour="settings-save"
                                            onClick={handleSaveSettings}
                                            className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                                        >
                                            Lưu thay đổi
                                        </button>
                                        {/* Optional Cancel/Reset button could go here, for now it just resets via re-rendering localProject on tab swap if we wanted to build that, but Save is enough */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CONTENT AREA */}
                    <div className="h-full overflow-y-auto p-6">

                        {/* TRANSLATE TAB */}
                        {validatedActiveTab === 'translate' && (
                            <TranslateTab
                                files={translateFiles}
                                synonymGroups={localProject.importedTerminology || []}
                                onUploadClick={() => setShowUploadModal(true)}
                                onRunFile={onRunTranslate}
                                onRunQC={onRunQC}
                                onDeleteFile={onDeleteFile}
                                onSelectFile={onSelectFile}
                                streamingCount={streamingCount}
                                totalSegments={totalSegments}
                                activeTranslatingFileId={activeTranslatingFileId}
                                onToggleFileQC={(fileId, skipQC) => {
                                    useProjectStore.getState().updateFile(fileId, { skipQC });
                                }}
                                onToggleAllQC={(currentlyAllChecked) => {
                                    const skip = currentlyAllChecked; // all checked → skip all
                                    for (const f of translateFiles) {
                                        useProjectStore.getState().updateFile(f.id, { skipQC: skip });
                                    }
                                }}
                                onRunAll={() => {
                                    // Commercial: dùng parent onRunAll (route qua CreditConfirmModal — accurate count + QC toggle inline)
                                    // Internal: legacy BatchSchedulerPanel (scheduler.ts char-based estimate, đang được phase out per US-04-10)
                                    if (isCommercial) {
                                        onRunAll();
                                        return;
                                    }
                                    const pending = translateFiles.filter(f =>
                                        f.translationStatus === 'pending' || f.translationStatus === 'error'
                                    );
                                    if (pending.length > 0) {
                                        const plan = createTranslateExecutionPlan(pending);
                                        useBatchSchedulerStore.getState().setPlan(plan);
                                        if (!showBatchPanel) toggleBatchPanel();
                                    }
                                }}
                            />
                        )}

                        {/* TRAIN TAB - combines file list + quality report */}
                        {validatedActiveTab === 'train' && (
                            <div className="max-w-5xl mx-auto">
                                {/* Train Tab Intro Banner */}
                                <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200 rounded-xl relative overflow-hidden group">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-orange-500 opacity-75"></div>
                                    <div className="flex items-start gap-4 relative z-10">
                                        <div className="w-12 h-12 bg-white/60 backdrop-blur border border-amber-200 rounded-xl flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform">
                                            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-amber-900 text-lg tracking-tight mb-1">Cá nhân hoá phong cách dịch AI</h3>
                                            <p className="text-sm text-amber-800/80 leading-relaxed max-w-3xl">
                                                Nạp tài liệu gốc (VI) kèm với bản dịch chuẩn của bạn (EN). Trí tuệ tự động sẽ chạy <strong className="font-semibold text-amber-900">Kiểm định chuyên sâu</strong> để phân tích sự chênh lệch và <strong className="font-semibold text-amber-900">tự cập nhật từ vựng mới</strong> phù hợp với phong cách của công ty bạn.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* AI disclaimer */}
                                <div className="mb-6 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>AI có thể sai sót — luôn rà soát kết quả Audit trước khi cập nhật từ vựng.</span>
                                </div>
                                {/* Actions */}
                                <div className="mb-6 flex items-center gap-3">
                                    <button
                                        onClick={() => setShowUploadModal(true)}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 transition-all shadow-md shadow-amber-200 hover:shadow-lg active:scale-95"
                                    >
                                        <FilePlus className="w-4 h-4" />
                                        Nạp tài liệu huấn luyện
                                    </button>
                                </div>

                                {/* Files Table */}
                                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                                        <h3 className="font-bold text-slate-700 flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-amber-500" />
                                            Hàng đợi huấn luyện
                                        </h3>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-slate-400 font-medium">
                                                {trainFiles.length} tài liệu
                                            </span>
                                            {(() => {
                                                const pendingTrain = trainFiles.filter((f: TranslationFile) => f.translationStatus !== 'done' || f.auditStatus !== 'done');
                                                return (
                                                    <button
                                                        onClick={() => {
                                                            if (pendingTrain.length === 0) return;
                                                            if (isCommercial) {
                                                                setShowTrainAll(true);
                                                            } else {
                                                                createPlanMutation.mutate(pendingTrain);
                                                                if (!showBatchPanel) toggleBatchPanel();
                                                            }
                                                        }}
                                                        disabled={pendingTrain.length === 0}
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-bold rounded-xl hover:bg-amber-700 transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-amber-600 shadow-sm shadow-amber-200"
                                                    >
                                                        <Zap className="w-4 h-4 fill-current" />
                                                        Bắt đầu Huấn luyện
                                                        {pendingTrain.length > 0 && (
                                                            <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-black bg-white/25 rounded-md tabular-nums">
                                                                {pendingTrain.length}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    <table className="w-full text-left">
                                        <thead className="bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-6 py-4 font-bold text-slate-500 uppercase tracking-wider text-xs whitespace-nowrap">
                                                    <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[200px]">Tên file tài liệu đã nạp vào dự án.</div>}>
                                                        <span className="inline-flex items-center gap-1 cursor-help">Tên tài liệu<HelpCircle className="w-3 h-3 text-slate-400" /></span>
                                                    </Tooltip>
                                                </th>
                                                <th className="px-6 py-4 font-bold text-slate-500 uppercase tracking-wider text-xs w-32 whitespace-nowrap">
                                                    <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[220px]">Trạng thái các bước: Dịch → Đối chiếu → Chấm điểm AI.</div>}>
                                                        <span className="inline-flex items-center gap-1 cursor-help">Tiến độ<HelpCircle className="w-3 h-3 text-slate-400" /></span>
                                                    </Tooltip>
                                                </th>
                                                <th className="px-6 py-4 font-bold text-slate-500 uppercase tracking-wider text-xs w-24 whitespace-nowrap">
                                                    <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[200px]">Điểm tổng hợp từ AI Audit (thang 0–10).</div>}>
                                                        <span className="inline-flex items-center gap-1 cursor-help">Chấm điểm<HelpCircle className="w-3 h-3 text-slate-400" /></span>
                                                    </Tooltip>
                                                </th>
                                                <th className="px-6 py-4 font-bold text-slate-500 uppercase tracking-wider text-xs w-40 text-right whitespace-nowrap">
                                                    <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[220px]">Bắt đầu, chạy lại hoặc xem báo cáo kiểm định chất lượng.</div>}>
                                                        <span className="inline-flex items-center gap-1 cursor-help justify-end">Phân tích<HelpCircle className="w-3 h-3 text-slate-400" /></span>
                                                    </Tooltip>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {trainFiles.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="px-6 py-16 text-center">
                                                        <div className="flex flex-col items-center gap-4">
                                                            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-200">
                                                                <FilePlus className="w-8 h-8 text-slate-300" />
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-600 font-bold text-lg mb-2">Chưa có tài liệu nào để học hỏi</p>
                                                                <div className="bg-white px-6 py-4 rounded-xl border border-slate-200 text-left w-full max-w-lg shadow-sm">
                                                                    <p className="text-slate-700 font-medium mb-3">Để hệ thống AI học đúng văn phong của bạn, vui lòng thao tác:</p>
                                                                    <ol className="list-decimal pl-5 text-slate-600 space-y-2 text-sm leading-relaxed">
                                                                        <li>Chuẩn bị <strong className="text-slate-800 inline-flex items-center gap-1">1 tài liệu {getLanguage(localProject.sourceLang || 'vi')?.countryCode && (
                                                                            <img src={`https://flagcdn.com/16x12/${getLanguage(localProject.sourceLang || 'vi')?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(localProject.sourceLang || 'vi')?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                                                        )} {getLanguageNameVi(localProject.sourceLang || 'vi')} gốc</strong> và <strong className="text-slate-800 inline-flex items-center gap-1">1 tài liệu {getLanguage(localProject.targetLang || 'en')?.countryCode && (
                                                                            <img src={`https://flagcdn.com/16x12/${getLanguage(localProject.targetLang || 'en')?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(localProject.targetLang || 'en')?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                                                        )} {getLanguageNameVi(localProject.targetLang || 'en')} đã dịch chuẩn</strong>.</li>
                                                                        <li>Cả 2 tài liệu nên cùng loại định dạng (PDF, DOCX, TXT...).</li>
                                                                        <li>Bấm nút <strong className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Nạp tài liệu huấn luyện</strong> ở phía trên để tải cặp file lên hệ thống.</li>
                                                                    </ol>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                trainFiles.map(file => {
                                                    const trainBusy = file.matchStatus === 'running' || file.translationStatus === 'running' || file.auditStatus === 'running';
                                                    return (
                                                    <tr key={file.id} className="hover:bg-slate-50 transition-colors group">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-start gap-3">
                                                                <button
                                                                    onClick={() => !trainBusy && onSelectFile(file.id)}
                                                                    disabled={trainBusy}
                                                                    className={`mt-0.5 p-1.5 rounded bg-slate-50 transition-colors border border-slate-200 ${
                                                                        trainBusy
                                                                            ? 'text-slate-300 cursor-not-allowed opacity-60'
                                                                            : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'
                                                                    }`}
                                                                    title={trainBusy ? 'Đang xử lý — chờ xong rồi xem' : 'Xem chi tiết'}
                                                                >
                                                                    <Eye className="w-4 h-4" />
                                                                </button>
                                                                <div>
                                                                    <div className="font-medium text-slate-800">{file.fileName}</div>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <div className="text-xs text-slate-400">{new Date(file.createdAt).toLocaleDateString()}</div>
                                                                        <div className="text-[10px] text-slate-300">|</div>
                                                                        <TokenBadge
                                                                            compact
                                                                            credits={selectPrimaryCredits(file.estimatedCredits, project.type === 'train' ? 'train' : 'translate')}
                                                                            accurateStatus={file.estimatedCredits ? 'ok' : 'idle'}
                                                                        />
                                                                        {(() => {
                                                                            const groups = localProject.synonymGroups || [];
                                                                            if (groups.length === 0) return null;
                                                                            const rules = trainGetMatchedRules(file);
                                                                            if (rules.length === 0) {
                                                                                return (
                                                                                    <span
                                                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[10px] font-medium text-slate-400"
                                                                                        title={`Project có ${groups.length} thuật ngữ nhưng file này không khớp với từ nào`}
                                                                                    >
                                                                                        <BookMarked className="w-2.5 h-2.5" />
                                                                                        0 thuật ngữ tùy chỉnh
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            const isLive = !file.matchedRules;
                                                                            return (
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); setTrainRulesModalFileId(file.id); }}
                                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-[10px] font-bold text-emerald-700 transition-colors"
                                                                                    title={isLive ? `Xem ${rules.length} thuật ngữ sẽ áp dụng khi dịch` : 'Xem thuật ngữ đã khớp'}
                                                                                >
                                                                                    <BookMarked className="w-2.5 h-2.5" />
                                                                                    {rules.length} thuật ngữ tùy chỉnh
                                                                                </button>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-col gap-1">
                                                                <StepStatus file={file} hasProjectBenchmark={!!project.humanBenchmark} />
                                                                {file.translationStatus === 'error' && (
                                                                    <span className="text-[10px] text-red-500 font-medium truncate max-w-[150px]" title={file.error}>
                                                                        Lỗi: {file.error}
                                                                    </span>
                                                                )}
                                                                {file.translationStatus === 'running' && (
                                                                    <span className="text-[10px] text-blue-500 font-medium animate-pulse">
                                                                        Đang dịch...
                                                                    </span>
                                                                )}
                                                                {file.auditStatus === 'running' && (
                                                                    <span className="text-[10px] text-blue-500 font-medium animate-pulse">
                                                                        Đang đánh giá...
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {(() => {
                                                                // Score lưu trong lightState (auditScore) → hiện ngay sau reload
                                                                // không cần chờ lazy-load auditResult full.
                                                                const score = file.auditScore ?? file.auditResult?.overallScore;
                                                                return score != null;
                                                            })() ? (
                                                                <span className={`font-bold ${getScoreColor((file.auditScore ?? file.auditResult?.overallScore)!)}`}>
                                                                    {file.auditScore ?? file.auditResult?.overallScore}/10
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-300">-</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center justify-end gap-2">
                                                                {(() => {
                                                                    // Use trainBusy from parent scope — covers matchStatus + translationStatus + auditStatus
                                                                    const running = trainBusy;
                                                                    // Dùng light fields (auditScore, auditStatus) — persist trên state.json sau refresh.
                                                                    // auditResult là heavy field, lazy-load → undefined trong list view → button sai.
                                                                    const hasResult = file.auditScore != null || file.auditStatus === 'done';
                                                                    if (running) {
                                                                        return (
                                                                            <button
                                                                                disabled
                                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-amber-50 text-amber-700 border border-amber-200 cursor-not-allowed whitespace-nowrap"
                                                                            >
                                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                                Đang chạy
                                                                            </button>
                                                                        );
                                                                    }
                                                                    if (hasResult) {
                                                                        return (
                                                                            <button
                                                                                onClick={() => onRunFile(file.id)}
                                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors whitespace-nowrap"
                                                                                title="Chạy lại Dịch & Đánh giá"
                                                                            >
                                                                                <RefreshCw className="w-3.5 h-3.5" />
                                                                                Chạy lại
                                                                            </button>
                                                                        );
                                                                    }
                                                                    if (file.translationStatus === 'error' || file.auditStatus === 'error') {
                                                                        return (
                                                                            <button
                                                                                onClick={() => onRunFile(file.id)}
                                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-amber-700 bg-amber-50 border border-amber-300 hover:bg-amber-100 transition-colors whitespace-nowrap"
                                                                                title="Thử lại"
                                                                            >
                                                                                <RefreshCw className="w-3.5 h-3.5" />
                                                                                Thử lại
                                                                            </button>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <button
                                                                            onClick={() => onRunFile(file.id)}
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-white bg-amber-600 hover:bg-amber-700 transition-colors shadow-sm shadow-amber-200 whitespace-nowrap"
                                                                            title="Chạy Dịch & Đánh giá"
                                                                        >
                                                                            <Play className="w-3.5 h-3.5 fill-current" />
                                                                            Bắt đầu
                                                                        </button>
                                                                    );
                                                                })()}
                                                                <button
                                                                    onClick={() => onDeleteFile(file.id)}
                                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                    title="Xóa tài liệu"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* QUALITY SECTION - show in dedicated Report tab */}
                        {activeTab === 'report' && projectFiles.some(f => f.auditResult) && (
                            <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-300">
                                {/* Dashboard Stats - Parsed from Comments */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                                    <div className={`${project.type === 'translate' ? 'bg-indigo-50 border-indigo-100 text-indigo-700' : 'bg-amber-50 border-amber-100 text-amber-700'} p-5 rounded-2xl flex items-center justify-between shadow-sm border`}>
                                        <div>
                                            <div className={`${project.type === 'translate' ? 'text-indigo-600' : 'text-amber-600'} text-xs font-bold uppercase tracking-wider mb-1`}>Thuật ngữ</div>
                                            <div className="text-3xl font-black">{stats.rule}</div>
                                        </div>
                                        <div className={`w-10 h-10 ${project.type === 'translate' ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'} rounded-full flex items-center justify-center`}>
                                            <BookOpen className="w-5 h-5" />
                                        </div>
                                    </div>
                                    <div className="bg-red-50 border border-red-100 p-5 rounded-2xl flex items-center justify-between shadow-sm">
                                        <div>
                                            <div className="text-red-600 text-xs font-bold uppercase tracking-wider mb-1">Lỗi</div>
                                            <div className="text-3xl font-black text-red-700">{stats.loi}</div>
                                        </div>
                                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                                            <XCircle className="w-5 h-5" />
                                        </div>
                                    </div>
                                    <div className="bg-orange-50 border border-orange-100 p-5 rounded-2xl flex items-center justify-between shadow-sm">
                                        <div>
                                            <div className="text-orange-600 text-xs font-bold uppercase tracking-wider mb-1">Cần xem lại</div>
                                            <div className="text-3xl font-black text-orange-700">{stats.canxemlai}</div>
                                        </div>
                                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">
                                            <AlertTriangle className="w-5 h-5" />
                                        </div>
                                    </div>
                                </div>

                                {/* 3-Column Layout: Lỗi | Cần xem lại | Rule */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    {/* Column 1: Rules */}
                                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                                        <div className={`px-4 py-3 border-b border-slate-200 ${project.type === 'translate' ? 'bg-indigo-50' : 'bg-amber-50'} flex items-center justify-between`}>
                                            <h3 className={`font-bold ${project.type === 'translate' ? 'text-indigo-700' : 'text-amber-700'} flex items-center gap-2 text-sm`}>
                                                <BookOpen className="w-4 h-4" />
                                                Thuật ngữ ({parsedRules.length})
                                            </h3>
                                            {parsedRules.length > 0 && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => {
                                                            const header = "Source\tTarget";
                                                            const body = parsedRules.map(r => `${r.source}\t${r.target}`).join('\n');
                                                            navigator.clipboard.writeText(`${header}\n${body}`);
                                                        }}
                                                        className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 uppercase"
                                                        title="Sao chép vào clipboard (tương thích Excel)"
                                                    >
                                                        Sao chép
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            // Get existing rules for duplicate check
                                                            const existingRules = new Set(
                                                                (localProject.terminologyRules || '').split('\n')
                                                                    .filter(l => l.trim())
                                                                    .map(l => {
                                                                        const parts = l.split(/\s*[\|→]\s*/);
                                                                        return `${parts[0]?.trim().toLowerCase()}|${parts[1]?.trim().toLowerCase()}`;
                                                                    })
                                                            );

                                                            setPendingRules(parsedRules.map(r => {
                                                                const key = `${r.source.trim().toLowerCase()}|${r.target.trim().toLowerCase()}`;
                                                                return {
                                                                    source: r.source,
                                                                    target: r.target,
                                                                    originalSource: r.source,
                                                                    originalTarget: r.target,
                                                                    selected: !existingRules.has(key), // Auto-deselect duplicates
                                                                    isDuplicate: existingRules.has(key)
                                                                };
                                                            }));
                                                            setShowAddToTerminologyModal(true);
                                                        }}
                                                        className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-800 uppercase flex items-center gap-1"
                                                        title="Thêm thuật ngữ đã chọn"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                        Thêm thuật ngữ
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                                            {parsedRules.map((rule, idx) => (
                                                <div key={idx} className="p-3 hover:bg-blue-50/30 transition-colors">
                                                    <div className="flex items-center gap-2 text-xs flex-wrap">
                                                        <span className="text-red-500 font-medium">{rule.source}</span>
                                                        <span className="text-slate-400">&rarr;</span>
                                                        <span className="text-emerald-600 font-bold">{rule.target}</span>
                                                        {rule.count > 1 && (
                                                            <span className="text-[9px] bg-blue-200 text-blue-700 px-1 py-0.5 rounded-full">{rule.count}x</span>
                                                        )}
                                                        {/* Tooltip for context */}
                                                        {(rule.occurrences || [rule]).length > 0 && (
                                                            <Tooltip
                                                                content={
                                                                    <div className="w-[600px] max-w-[90vw] bg-white">
                                                                        {(rule.occurrences || [rule]).slice(0, 5).map((occ, i) => (
                                                                            <div key={i} className={`p-3 ${i > 0 ? 'border-t border-slate-200' : ''}`}>
                                                                                <div className="flex items-center justify-between mb-2">
                                                                                    <div className="text-[10px] font-bold text-slate-500 uppercase">
                                                                                        Segment #{occ.segmentIndex} • <span className="text-indigo-600">{occ.fileName}</span>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="grid grid-cols-3 gap-3">
                                                                                    <div className="p-2 bg-slate-50 rounded text-xs border border-slate-200">
                                                                                        <div className="font-bold text-slate-500 text-[10px] uppercase mb-1">Nguồn</div>
                                                                                        <div className="text-xs text-slate-700">
                                                                                            <HighlightText text={occ.sourceText || '-'} highlight={rule.source} color="bg-orange-200" />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="p-2 bg-blue-50 rounded text-xs border border-blue-200">
                                                                                        <div className="font-bold text-blue-600 text-[10px] uppercase mb-1">AI</div>
                                                                                        <div className="text-slate-700 whitespace-pre-wrap">
                                                                                            <HighlightText text={occ.aiText || '-'} highlight={rule.aiTerm || ''} color="bg-blue-200" />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="p-2 bg-emerald-50 rounded text-xs border border-emerald-200">
                                                                                        <div className="font-bold text-emerald-600 text-[10px] uppercase mb-1">Người dịch</div>
                                                                                        <div className="text-slate-700 whitespace-pre-wrap">
                                                                                            <HighlightText text={occ.humanText || '-'} highlight={rule.target} color="bg-emerald-200" />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                        {(rule.occurrences || [rule]).length > 5 && (
                                                                            <div className="p-2 text-center text-xs text-slate-400 bg-slate-50 border-t border-slate-200">
                                                                                + {(rule.occurrences || [rule]).length - 5} lần xuất hiện nữa...
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                }
                                                            >
                                                                <Eye className="w-3 h-3 text-slate-400 hover:text-blue-600 cursor-pointer ml-auto" />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 mt-1 truncate">
                                                        {rule.files.join(', ')}
                                                    </div>
                                                </div>
                                            ))}
                                            {parsedRules.length === 0 && (
                                                <div className="p-4 text-center text-slate-400 text-xs italic">Không có rule</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Column 2: Lỗi */}
                                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                                        <div className="px-4 py-3 border-b border-slate-200 bg-red-50">
                                            <h3 className="font-bold text-red-700 flex items-center gap-2 text-sm">
                                                <XCircle className="w-4 h-4" />
                                                Lỗi ({parsedComments.loi.length})
                                            </h3>
                                        </div>
                                        <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                                            {parsedComments.loi.map((item, idx) => (
                                                <div key={idx} className="p-3 hover:bg-red-50/30 transition-colors">
                                                    <div className="flex items-start gap-2">
                                                        <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                                            LỖI
                                                        </span>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="text-xs text-slate-700 whitespace-pre-wrap">{item.content}</div>
                                                                {(item.sourceText || item.aiText) && (
                                                                    <Tooltip
                                                                        content={
                                                                            <div className="w-[800px] max-w-[90vw] p-4 bg-white">
                                                                                <div className="grid grid-cols-3 gap-3">
                                                                                    <div className="p-2 bg-slate-50 rounded text-xs border border-slate-200">
                                                                                        <div className="font-bold text-slate-500 text-[10px] uppercase mb-1">Nguồn</div>
                                                                                        <div className="text-slate-700">{item.sourceText || '-'}</div>
                                                                                    </div>
                                                                                    <div className="p-2 bg-blue-50 rounded text-xs border border-blue-200">
                                                                                        <div className="font-bold text-blue-600 text-[10px] uppercase mb-1">AI</div>
                                                                                        <div className="text-slate-700">{item.aiText || '-'}</div>
                                                                                    </div>
                                                                                    <div className="p-2 bg-emerald-50 rounded text-xs border border-emerald-200">
                                                                                        <div className="font-bold text-emerald-600 text-[10px] uppercase mb-1">Người dịch</div>
                                                                                        <div className="text-slate-700">{item.humanText || '-'}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <Eye className="w-3 h-3 text-slate-400 hover:text-indigo-600 cursor-pointer shrink-0" />
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                            <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                                                <span>#{item.segmentIndex}</span>
                                                                <span>•</span>
                                                                <button onClick={() => onSelectFile(item.fileId)} className="hover:text-indigo-600 underline truncate max-w-[80px]">
                                                                    {item.fileName}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {parsedComments.loi.length === 0 && (
                                                <div className="p-4 text-center text-slate-400 text-xs italic">Không có lỗi</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Column 3: Cần xem lại */}
                                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                                        <div className="px-4 py-3 border-b border-slate-200 bg-orange-50">
                                            <h3 className="font-bold text-orange-700 flex items-center gap-2 text-sm">
                                                <AlertTriangle className="w-4 h-4" />
                                                Cần xem lại ({parsedComments.canxemlai.length})
                                            </h3>
                                        </div>
                                        <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                                            {parsedComments.canxemlai.map((item, idx) => (
                                                <div key={idx} className="p-3 hover:bg-orange-50/30 transition-colors">
                                                    <div className="flex items-start gap-2">
                                                        <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                                                            XEM
                                                        </span>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="text-xs text-slate-700 whitespace-pre-wrap">{item.content}</div>
                                                                {(item.sourceText || item.aiText) && (
                                                                    <Tooltip
                                                                        align="right"
                                                                        content={
                                                                            <div className="w-[800px] max-w-[90vw] p-4 bg-white">
                                                                                <div className="grid grid-cols-3 gap-3">
                                                                                    <div className="p-2 bg-slate-50 rounded text-xs border border-slate-200">
                                                                                        <div className="font-bold text-slate-500 text-[10px] uppercase mb-1">Nguồn</div>
                                                                                        <div className="text-slate-700">{item.sourceText || '-'}</div>
                                                                                    </div>
                                                                                    <div className="p-2 bg-blue-50 rounded text-xs border border-blue-200">
                                                                                        <div className="font-bold text-blue-600 text-[10px] uppercase mb-1">AI</div>
                                                                                        <div className="text-slate-700">{item.aiText || '-'}</div>
                                                                                    </div>
                                                                                    <div className="p-2 bg-emerald-50 rounded text-xs border border-emerald-200">
                                                                                        <div className="font-bold text-emerald-600 text-[10px] uppercase mb-1">Người dịch</div>
                                                                                        <div className="text-slate-700">{item.humanText || '-'}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <Eye className="w-3 h-3 text-slate-400 hover:text-indigo-600 cursor-pointer shrink-0" />
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                            <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                                                <span>#{item.segmentIndex}</span>
                                                                <span>•</span>
                                                                <button onClick={() => onSelectFile(item.fileId)} className="hover:text-indigo-600 underline truncate max-w-[80px]">
                                                                    {item.fileName}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {parsedComments.canxemlai.length === 0 && (
                                                <div className="p-4 text-center text-slate-400 text-xs italic">Không có vấn đề cần xem lại</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Empty State for all columns */}
                                {
                                    parsedComments.loi.length === 0 && parsedComments.canxemlai.length === 0 && parsedRules.length === 0 && (
                                        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center mt-4">
                                            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
                                            <p className="text-lg font-medium text-slate-600">Bản dịch hoàn hảo!</p>
                                            <p className="text-slate-400">Không có lỗi hoặc gợi ý nào.</p>
                                        </div>
                                    )
                                }
                            </div >
                        )}

                        {/* REPORT TAB — empty state khi chưa có audit nào */}
                        {activeTab === 'report' && !projectFiles.some(f => f.auditResult) && (
                            <div className="max-w-2xl mx-auto py-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="relative bg-gradient-to-br from-amber-50 via-orange-50/60 to-amber-50 border border-amber-200 rounded-3xl p-10 text-center overflow-hidden shadow-sm">
                                    {/* Decorative blobs */}
                                    <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-200/40 rounded-full blur-3xl pointer-events-none" />
                                    <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-orange-200/30 rounded-full blur-3xl pointer-events-none" />

                                    <div className="relative">
                                        {/* Icon cluster — sparkle around an empty book */}
                                        <div className="relative w-24 h-24 mx-auto mb-6">
                                            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-3xl rotate-6 opacity-20" />
                                            <div className="absolute inset-0 bg-white rounded-3xl border-2 border-amber-200 flex items-center justify-center shadow-md">
                                                <BookOpen className="w-10 h-10 text-amber-400" />
                                            </div>
                                            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-500 animate-pulse" />
                                            <Sparkles className="absolute -bottom-1 -left-1 w-4 h-4 text-orange-400 animate-pulse" style={{ animationDelay: '0.4s' }} />
                                        </div>

                                        <h3 className="text-2xl font-black text-amber-900 tracking-tight mb-2">
                                            Chưa có báo cáo chất lượng
                                        </h3>
                                        <p className="text-sm text-amber-800/80 leading-relaxed mb-1 max-w-md mx-auto">
                                            AI cần "đọc" qua tài liệu mẫu của bạn trước khi có thể chấm điểm chất lượng.
                                        </p>
                                        <p className="text-sm text-amber-800/80 leading-relaxed mb-7 max-w-md mx-auto">
                                            Hãy nạp <strong className="font-semibold text-amber-900">cặp tài liệu song ngữ</strong> rồi chạy <strong className="font-semibold text-amber-900">huấn luyện</strong> — báo cáo sẽ tự xuất hiện ở đây ✨
                                        </p>

                                        <button
                                            onClick={() => setActiveTab('train')}
                                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-300/40 hover:shadow-xl active:scale-95"
                                        >
                                            <Zap className="w-4 h-4 fill-current" />
                                            Bắt đầu huấn luyện
                                            <ArrowRight className="w-4 h-4" />
                                        </button>

                                        <p className="mt-5 text-[11px] text-amber-700/60 italic">
                                            Mỗi báo cáo giúp AI học sâu hơn về văn phong của bạn.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* GLOSSARY TAB */}
                        {validatedActiveTab === 'glossary' && (
                            <div className="flex flex-col h-full">
                                {/* Back to File Banner */}
                                {returnToFileId && onReturnToFile && (
                                    <div className="mb-4 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between">
                                        <span className="text-sm text-indigo-700">
                                            Bạn đến từ một tài liệu. Chỉnh sửa thuật ngữ rồi quay lại.
                                        </span>
                                        <button
                                            onClick={() => onReturnToFile(returnToFileId)}
                                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider rounded-lg transition-colors flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                                            </svg>
                                            Quay lại tài liệu
                                        </button>
                                    </div>
                                )}

                                {/* Thuật ngữ đã chọn Header */}
                                <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <BookOpen className="w-5 h-5 text-emerald-600" />
                                        <div>
                                            <p className="text-sm font-bold text-slate-800">Thuật ngữ đã chọn</p>
                                            <p className="text-xs text-slate-500">
                                                {(localProject.importedTerminology?.length || 0)} nhóm từ Bảng thuật ngữ chung
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                    </div>
                                </div>

                                <TerminologyTab
                                    synonymGroups={localProject.synonymGroups}
                                    onUpdateSynonymGroups={(groups) => {
                                        const updated = {
                                            ...localProject,
                                            synonymGroups: groups,
                                        };
                                        setLocalProject(updated);
                                        onUpdateProject(updated);

                                        // DIRECT DISK SAVE - bypass auto-save to ensure persistence
                                        const rootHandle = useFileSystemStore.getState().rootHandle;
                                        if (rootHandle) {
                                            (fileSystemPersistence as any).handle = rootHandle;
                                            const category = project.type === 'translate' ? 'translating' : project.type === 'train' ? 'training' : '';
                                            const folderName = `project_${sanitizeFolderName(project.name, project.id)}`;
                                            const path = category ? [category, folderName] : [folderName];
                                            const { files: _f, ...metadata } = updated as any;
                                            fileSystemPersistence.saveJson(path, 'project.json', metadata)
                                                .then(() => console.log('[SynonymGroups] Saved to disk:', groups.length, 'groups'))
                                                .catch(err => console.error('[SynonymGroups] Disk save failed:', err));
                                        }
                                    }}
                                    // Phase 2+3: Global Glossary integration
                                    globalSynonymGroups={globalSynonymGroups}
                                    projectId={project.id}
                                    projectName={project.name}
                                    onAddToGlobal={(newGroups) => {
                                        const glossaryStore = useGlossaryStore.getState();
                                        newGroups.forEach(g => glossaryStore.addGroup(g));
                                    }}
                                    onPickFromGlobal={() => setShowTermSelector(true)}
                                    defaultSourceLang={localProject.sourceLang}
                                    defaultTargetLang={localProject.targetLang}
                                />
                            </div>
                        )}

                        {/* TERMINOLOGY TAB (Translate Projects) — reuse TerminologyTab full CRUD */}
                        {validatedActiveTab === 'terminology' && (
                            <div className="flex flex-col h-full">
                                <TerminologyTab
                                    synonymGroups={
                                        (localProject.importedTerminology && localProject.importedTerminology.length > 0)
                                            ? localProject.importedTerminology
                                            : (localProject.id === DEMO_PROJECT_ID ? DEMO_TERMS : [])
                                    }
                                    onUpdateSynonymGroups={(groups) => {
                                        const updated = { ...localProject, importedTerminology: groups };
                                        setLocalProject(updated);
                                        onUpdateProject(updated);

                                        // DIRECT DISK SAVE — match pattern của Train tab
                                        const rootHandle = useFileSystemStore.getState().rootHandle;
                                        if (rootHandle) {
                                            (fileSystemPersistence as any).handle = rootHandle;
                                            const category = project.type === 'translate' ? 'translating' : project.type === 'train' ? 'training' : '';
                                            const folderName = `project_${sanitizeFolderName(project.name, project.id)}`;
                                            const path = category ? [category, folderName] : [folderName];
                                            const { files: _f, ...metadata } = updated as any;
                                            fileSystemPersistence.saveJson(path, 'project.json', metadata)
                                                .then(() => console.log('[ImportedTerminology] Saved to disk:', groups.length, 'groups'))
                                                .catch(err => console.error('[ImportedTerminology] Disk save failed:', err));
                                        }
                                    }}
                                    globalSynonymGroups={globalSynonymGroups}
                                    projectId={project.id}
                                    projectName={project.name}
                                    onPickFromGlobal={() => setShowTermSelector(true)}
                                    defaultSourceLang={localProject.sourceLang}
                                    defaultTargetLang={localProject.targetLang}
                                    /* KHÔNG pass onAddToGlobal — Translate tab terms vốn ĐẾN TỪ Global, không "đẩy lên" lại */
                                />
                            </div>
                        )}
                    </div >
                </div >

                {/* Batch Upload Modal */}
                < BatchUploadModal
                    isOpen={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    onImport={onBatchImport}
                    mode={validatedActiveTab === 'translate' ? 'translate' : 'train'}
                    sourceLang={localProject.sourceLang}
                    targetLang={localProject.targetLang}
                />

                {/* Add to Terminology Modal */}
                {
                    showAddToTerminologyModal && (() => {
                        const selectedCount = pendingRules.filter(r => r.selected).length;
                        return (
                        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                                {/* Header */}
                                <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md shadow-emerald-500/20 flex items-center justify-center shrink-0">
                                            <BookMarked className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-black text-slate-900 tracking-tight">Thêm vào từ điển</h2>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                Đã chọn <span className="font-bold text-emerald-700 tabular-nums">{selectedCount}</span> / {pendingRules.length} quy tắc — chỉnh sửa nếu cần trước khi thêm
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={() => setShowAddToTerminologyModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors">
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Toolbar — actions + group selector cùng row */}
                                <div className="px-6 py-3 bg-slate-50/60 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0 flex-wrap">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setPendingRules(prev => prev.map(r => ({ ...r, selected: true })))}
                                            className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-white rounded-md transition-colors"
                                        >
                                            Chọn tất cả
                                        </button>
                                        <button
                                            onClick={() => setPendingRules(prev => prev.map(r => ({ ...r, selected: false })))}
                                            className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-white rounded-md transition-colors"
                                        >
                                            Bỏ chọn
                                        </button>
                                        <button
                                            onClick={() => setPendingRules(prev => prev.filter(r => r.selected))}
                                            className="px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        >
                                            Xoá mục chưa chọn
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Thêm vào nhóm</span>
                                        {selectedTargetGroupId === '__new__' ? (
                                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-50 border-2 border-emerald-300">
                                                <Plus className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    placeholder="Tên nhóm mới..."
                                                    className="text-sm bg-transparent min-w-[140px] focus:outline-none placeholder:text-emerald-500/50"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                                                            setSelectedTargetGroupId((e.target as HTMLInputElement).value.trim());
                                                        }
                                                        if (e.key === 'Escape') {
                                                            setSelectedTargetGroupId('');
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const name = e.target.value.trim();
                                                        if (name) {
                                                            setSelectedTargetGroupId(name);
                                                        } else {
                                                            setSelectedTargetGroupId('');
                                                        }
                                                    }}
                                                />
                                                <button
                                                    onClick={() => setSelectedTargetGroupId('')}
                                                    className="p-0.5 text-emerald-600 hover:text-red-600 rounded transition-colors"
                                                    title="Huỷ"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="relative group">
                                                <select
                                                    value={selectedTargetGroupId}
                                                    onChange={(e) => setSelectedTargetGroupId(e.target.value)}
                                                    className={`appearance-none cursor-pointer pl-3 pr-8 py-1.5 text-sm font-semibold rounded-lg border-2 outline-none transition-all min-w-[180px] ${
                                                        selectedTargetGroupId
                                                            ? 'bg-emerald-50 border-emerald-400 text-emerald-800 hover:bg-emerald-100'
                                                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                                                    }`}
                                                >
                                                    <option value="">Chưa phân loại</option>
                                                    {Array.from(new Set(
                                                        (localProject.synonymGroups || [])
                                                            .map(g => g.category)
                                                            .filter((c): c is string => !!c && c.trim() !== '')
                                                    )).sort().map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                    <option value="__new__">+ Tạo nhóm mới...</option>
                                                </select>
                                                <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${
                                                    selectedTargetGroupId ? 'text-emerald-600' : 'text-slate-400'
                                                }`} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Rules table — banded rows, no per-row borders */}
                                <div className="flex-1 overflow-y-auto">
                                    {pendingRules.length === 0 ? (
                                        <div className="text-center py-16 text-slate-400 italic text-sm">
                                            Không có quy tắc nào để thêm.
                                        </div>
                                    ) : (
                                        <table className="w-full">
                                            <thead className="sticky top-0 z-[1] bg-white border-b border-slate-200">
                                                <tr>
                                                    <th className="w-12 px-4 py-2.5"></th>
                                                    <th className="text-left px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500">Từ gốc (VI)</th>
                                                    <th className="w-6"></th>
                                                    <th className="text-left px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500">Bản dịch (EN)</th>
                                                    <th className="w-32 px-3 py-2.5"></th>
                                                    <th className="w-12 px-3 py-2.5"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingRules.map((rule, idx) => {
                                                    const isEdited = rule.source !== rule.originalSource || rule.target !== rule.originalTarget;
                                                    const rowBg = rule.isDuplicate
                                                        ? 'bg-amber-50/60 hover:bg-amber-50'
                                                        : isEdited
                                                            ? 'bg-blue-50/60 hover:bg-blue-50'
                                                            : !rule.selected
                                                                ? 'bg-slate-50/40 hover:bg-slate-50 opacity-60'
                                                                : idx % 2 === 0
                                                                    ? 'bg-white hover:bg-emerald-50/30'
                                                                    : 'bg-slate-50/50 hover:bg-emerald-50/30';
                                                    return (
                                                        <tr key={idx} className={`transition-colors ${rowBg}`}>
                                                            <td className="px-4 py-2 text-center">
                                                                <button
                                                                    onClick={() => setPendingRules(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r))}
                                                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                                                                        rule.selected ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-slate-300 hover:border-slate-400'
                                                                    }`}
                                                                >
                                                                    {rule.selected && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                                                                </button>
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <input
                                                                    type="text"
                                                                    value={rule.source}
                                                                    onChange={(e) => setPendingRules(prev => prev.map((r, i) => i === idx ? { ...r, source: e.target.value } : r))}
                                                                    className="w-full px-2 py-1 text-sm bg-transparent border-0 border-b border-transparent focus:border-emerald-400 outline-none transition-colors"
                                                                    placeholder="Từ gốc"
                                                                />
                                                            </td>
                                                            <td className="text-center text-slate-300 text-xs">→</td>
                                                            <td className="px-3 py-2">
                                                                <input
                                                                    type="text"
                                                                    value={rule.target}
                                                                    onChange={(e) => setPendingRules(prev => prev.map((r, i) => i === idx ? { ...r, target: e.target.value } : r))}
                                                                    className="w-full px-2 py-1 text-sm bg-transparent border-0 border-b border-transparent font-medium focus:border-emerald-400 outline-none transition-colors"
                                                                    placeholder="Bản dịch"
                                                                />
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <div className="flex items-center gap-1 justify-end">
                                                                    {rule.isDuplicate && (
                                                                        <span className="text-[9px] px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded font-bold uppercase tracking-wider">Trùng</span>
                                                                    )}
                                                                    {isEdited && (
                                                                        <span className="text-[9px] px-1.5 py-0.5 bg-blue-200 text-blue-800 rounded font-bold uppercase tracking-wider">Đã sửa</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-2 text-center">
                                                                <button
                                                                    onClick={() => setPendingRules(prev => prev.filter((_, i) => i !== idx))}
                                                                    className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                    title="Xoá quy tắc"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 shrink-0 bg-white">
                                    <span className="text-sm text-slate-500">
                                        <span className="font-bold text-slate-800 tabular-nums">{selectedCount}</span> quy tắc sẽ được thêm
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setShowAddToTerminologyModal(false)}
                                            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                        >
                                            Hủy
                                        </button>
                                        <button
                                            onClick={() => {
                                                const selectedRules = pendingRules.filter(r => r.selected && r.source.trim() && r.target.trim());
                                                if (selectedRules.length === 0) return;

                                                // Deduplicate against existing synonymGroups
                                                const existingSynonymGroups = localProject.synonymGroups || [];
                                                const uniqueSelectedRules = selectedRules.filter(r => {
                                                    const srcLower = r.source.trim().toLowerCase();
                                                    const tgtLower = r.target.trim().toLowerCase();
                                                    return !existingSynonymGroups.some(g =>
                                                        g.sources.some(s => s.toLowerCase() === srcLower) &&
                                                        g.targets.some(t => t.text.toLowerCase() === tgtLower)
                                                    );
                                                });

                                                if (uniqueSelectedRules.length === 0) {
                                                    setShowAddToTerminologyModal(false);
                                                    setActiveTab(project.type === 'train' ? 'glossary' : 'terminology');
                                                    return;
                                                }

                                                // selectedTargetGroupId stores category name directly ('' = uncategorized)
                                                const targetCategory = selectedTargetGroupId && selectedTargetGroupId !== '__new__'
                                                    ? selectedTargetGroupId
                                                    : undefined;

                                                // Create SynonymGroup entries (single source of truth)
                                                // Lang pair inherit từ project — báo cáo chất lượng tạo từ Train có sourceLang/targetLang
                                                const newSynonymGroups: SynonymGroup[] = uniqueSelectedRules.map(r => ({
                                                    id: crypto.randomUUID(),
                                                    name: r.target.trim(),
                                                    sources: [r.source.trim()],
                                                    targets: [{ text: r.target.trim(), isDefault: true }],
                                                    category: targetCategory,
                                                    sourceLang: localProject.sourceLang,
                                                    targetLang: localProject.targetLang,
                                                }));

                                                // Merge: if a synonym group with the same name+category exists, add source to it
                                                const mergedSynonymGroups = [...existingSynonymGroups];
                                                for (const newGroup of newSynonymGroups) {
                                                    const existing = mergedSynonymGroups.find(
                                                        g => g.name.toLowerCase() === newGroup.name.toLowerCase()
                                                            && (g.category || '') === (newGroup.category || '')
                                                    );
                                                    if (existing) {
                                                        const existingSources = new Set(existing.sources.map(s => s.toLowerCase()));
                                                        for (const src of newGroup.sources) {
                                                            if (!existingSources.has(src.toLowerCase())) {
                                                                existing.sources.push(src);
                                                            }
                                                        }
                                                    } else {
                                                        mergedSynonymGroups.push(newGroup);
                                                    }
                                                }

                                                const updated = {
                                                    ...localProject,
                                                    synonymGroups: mergedSynonymGroups,
                                                };

                                                setLocalProject(updated);
                                                onUpdateProject(updated);
                                                setShowAddToTerminologyModal(false);

                                                // Switch to glossary tab to review added rules
                                                setActiveTab(project.type === 'train' ? 'glossary' : 'terminology');
                                            }}
                                            disabled={pendingRules.filter(r => r.selected).length === 0}
                                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            <Check className="w-4 h-4" />
                                            Thêm {pendingRules.filter(r => r.selected).length} quy tắc
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        );
                    })()
                }

                {/* Batch Execution Panel */}
                {
                    showBatchPanel && (
                        <BatchExecutionPanel
                            onClose={toggleBatchPanel}
                            onExecute={() => {
                                if (currentPlan) {
                                    executePlanMutation.mutate(currentPlan);
                                }
                            }}
                            onPause={() => {
                                useBatchSchedulerStore.getState().pause();
                            }}
                            onResume={() => {
                                useBatchSchedulerStore.getState().resume();
                            }}
                            onCancel={() => {
                                useBatchSchedulerStore.getState().cancel();
                                toggleBatchPanel();
                            }}
                        />
                    )
                }

                {/* Run-all Train modal (commercial only) */}
                {isCommercial && showTrainAll && (() => {
                    const pendingTrain = trainFiles.filter((f: TranslationFile) => f.translationStatus !== 'done' || f.auditStatus !== 'done');
                    return (
                        <RunAllModal
                            isOpen={showTrainAll}
                            onClose={() => setShowTrainAll(false)}
                            pendingFiles={pendingTrain}
                            creditsAvailable={creditBalance?.credits ?? 0}
                            mode="train"
                            onBuyCredits={() => {
                                setShowTrainAll(false);
                                if (typeof window !== 'undefined') window.location.assign('/app/shop');
                            }}
                            onConfirm={(selectedIds) => {
                                setShowTrainAll(false);
                                if (selectedIds.length === 0) return;
                                const selected = pendingTrain.filter(f => selectedIds.includes(f.id));
                                createPlanMutation.mutate(selected);
                                if (!showBatchPanel) toggleBatchPanel();
                            }}
                        />
                    );
                })()}
            </div >

            {/* Term Selector Modal */}
            < TermSelector
                isOpen={showTermSelector}
                onClose={() => setShowTermSelector(false)}
                currentSelection={
                    project.type === 'train'
                        ? localProject.synonymGroups || []
                        : localProject.importedTerminology || []
                }
                onConfirm={(selectedGroups) => {
                    let updated;
                    if (project.type === 'train') {
                        // Train: merge into synonymGroups (avoid duplicates by id)
                        const existingIds = new Set((localProject.synonymGroups || []).map(g => g.id));
                        const newGroups = selectedGroups.filter(g => !existingIds.has(g.id));
                        const merged = [...(localProject.synonymGroups || []), ...newGroups];
                        updated = { ...localProject, synonymGroups: merged };
                    } else {
                        // Translate: set importedTerminology
                        updated = { ...localProject, importedTerminology: selectedGroups };
                    }
                    setLocalProject(updated);
                    onUpdateProject(updated);
                    persistProjectMetadata(updated, 'TermSelector.onConfirm');
                }}
            />

            {/* Manual Term Editor Modal */}
            <SynonymGroupEditor
                isOpen={showGroupEditor}
                onCancel={() => setShowGroupEditor(false)}
                onSave={(newGroup: SynonymGroup) => {
                    let updated;
                    if (project.type === 'train') {
                        const merged = [...(localProject.synonymGroups || []), newGroup];
                        updated = { ...localProject, synonymGroups: merged };
                    } else {
                        const merged = [...(localProject.importedTerminology || []), newGroup];
                        updated = { ...localProject, importedTerminology: merged };
                    }
                    setLocalProject(updated);
                    onUpdateProject(updated);
                    persistProjectMetadata(updated, 'SynonymGroupEditor.onSave');
                    setShowGroupEditor(false);
                }}
            />

            {/* Train tab: Matched terminology rules modal */}
            {trainRulesModalFileId && (() => {
                const modalFile = trainFiles.find(f => f.id === trainRulesModalFileId);
                if (!modalFile) return null;
                const modalRules = trainGetMatchedRules(modalFile);
                return (
                    <div
                        className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                        onClick={() => setTrainRulesModalFileId(null)}
                    >
                        <div
                            className="bg-white rounded-xl shadow-2xl w-[640px] max-w-full max-h-[80vh] flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <div>
                                    <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                        <BookMarked className="w-5 h-5 text-emerald-600" />
                                        Thuật ngữ đã khớp
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{modalFile.fileName}</p>
                                </div>
                                <button
                                    onClick={() => setTrainRulesModalFileId(null)}
                                    className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                {modalRules.length === 0 ? (
                                    <div className="text-center py-12 text-slate-400 italic text-sm">Chưa có thuật ngữ nào được khớp.</div>
                                ) : (
                                    <div className="space-y-1.5">
                                        {modalRules.map((rule, idx) => (
                                            <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                                                <span className="text-[10px] font-mono text-slate-400 w-6 shrink-0 tabular-nums">{idx + 1}</span>
                                                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium border border-indigo-100">{rule.source}</span>
                                                <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                                                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium border border-amber-200">{rule.target}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                                <span className="text-xs text-slate-500">Tổng cộng: <span className="font-bold text-slate-700">{modalRules.length}</span> thuật ngữ</span>
                                <button
                                    onClick={() => setTrainRulesModalFileId(null)}
                                    className="px-4 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                                >
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* US-17-06: Industry preset prompt modal */}
            {isCommercial && (
                <IndustryPresetModal
                    isOpen={showPresetModal}
                    currentIndustry={(localProject.industry as any) || null}
                    hasExistingPrompt={!!(localProject.systemPrompt && localProject.systemPrompt.trim().length > 0)}
                    onClose={() => setShowPresetModal(false)}
                    onApply={(preset: IndustryPreset) => {
                        const updated = {
                            ...localProject,
                            systemPrompt: preset.prompt,
                            industry: preset.id,
                        };
                        setLocalProject(updated);
                        onUpdateProject(updated);
                        setShowPresetModal(false);
                    }}
                />
            )}

            {/* Fullscreen system prompt editor */}
            {showPromptFullscreen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-150">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                            <div>
                                <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-indigo-500" />
                                    Chỉ thị cho AI — chế độ phóng to
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">{(localProject.systemPrompt || '').length} ký tự · Esc hoặc bấm "Đóng" để thoát.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowPromptFullscreen(false)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-100 transition-all"
                            >
                                <Minimize2 className="w-3.5 h-3.5" />
                                Đóng
                            </button>
                        </div>
                        <textarea
                            autoFocus
                            value={localProject.systemPrompt}
                            onChange={(e) => setLocalProject({ ...localProject, systemPrompt: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Escape') setShowPromptFullscreen(false); }}
                            className="flex-1 w-full px-6 py-4 outline-none resize-none text-sm font-mono leading-relaxed text-slate-800"
                            placeholder="Nhập chỉ thị cho AI..."
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default ProjectDetail;
